import styled from 'styled-components';

/**
 * Styled component wrapper for markdown content in chat messages
 * Handles formatting: paragraphs, links, bold, italic, lists, headings, blockquotes
 * Optimized to reduce excessive whitespace and maintain readability
 */
export const MarkdownContent = styled.div`
  /* Override parent white-space to allow HTML rendering */
  white-space: normal;

  /* Paragraph spacing - minimal to match chat style */
  .markdown-p {
    margin: 0;
    line-height: 1.5;
    display: inline;
  }

  .markdown-p + .markdown-p {
    display: block;
    margin-top: 0.5em;
  }

  /* Remove br spacing */
  br {
    display: none;
  }

  /* Links - consistent with app theme */
  .markdown-link {
    color: #1890ff;
    text-decoration: underline;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #40a9ff;
    }

    &:active {
      color: #096dd9;
    }
  }

  /* Bold text */
  .markdown-strong {
    font-weight: 600;
    color: inherit;
  }

  /* Italic text */
  .markdown-em {
    font-style: italic;
    color: inherit;
  }

  /* Lists - compact spacing with hierarchy support */
  .markdown-list {
    margin: 0.5em 0;
    padding-left: 1.2em;
    list-style-type: disc;
  }

  .markdown-list:first-child {
    margin-top: 0;
  }

  .markdown-list:last-child {
    margin-bottom: 0;
  }

  /* Level 1: Main list - disc bullets */
  .markdown-list-1 {
    list-style-type: disc;
    margin: 0.5em 0;
    padding-left: 1.2em;
  }

  /* Level 2: Nested list - circle bullets with indentation */
  .markdown-list-2 {
    list-style-type: circle;
    margin: 0.2em 0;
    padding-left: 1.5em;
  }

  /* Level 3: Deep nested - square bullets */
  .markdown-list-3 {
    list-style-type: square;
    margin: 0.2em 0;
    padding-left: 1.5em;
  }

  /* List items - minimal spacing */
  .markdown-li {
    margin: 0.1em 0;
    line-height: 1.5;
    display: list-item;
  }

  /* Nested lists general override (fallback) */
  .markdown-list .markdown-list {
    margin: 0.2em 0;
    padding-left: 1.5em;
    list-style-type: circle;
  }

  /* Headings - hierarchy with proper spacing */
  .markdown-h1 {
    font-size: 1.5em;
    font-weight: 600;
    margin: 0.67em 0 0.5em 0;
    line-height: 1.3;
    color: inherit;
  }

  .markdown-h2 {
    font-size: 1.3em;
    font-weight: 600;
    margin: 0.75em 0 0.5em 0;
    line-height: 1.3;
    color: inherit;
  }

  .markdown-h3 {
    font-size: 1.1em;
    font-weight: 600;
    margin: 0.83em 0 0.5em 0;
    line-height: 1.3;
    color: inherit;
  }

  .markdown-h4,
  .markdown-h5,
  .markdown-h6 {
    font-size: 1em;
    font-weight: 600;
    margin: 0.5em 0;
    line-height: 1.3;
    color: inherit;
  }

  /* First heading no top margin */
  .markdown-h1:first-child,
  .markdown-h2:first-child,
  .markdown-h3:first-child,
  .markdown-h4:first-child,
  .markdown-h5:first-child,
  .markdown-h6:first-child {
    margin-top: 0;
  }

  /* Blockquotes - styled for emphasis */
  .markdown-blockquote {
    border-left: 4px solid #d9d9d9;
    padding-left: 1em;
    margin: 0.5em 0;
    color: #666;
    font-style: italic;
  }

  .markdown-blockquote:first-child {
    margin-top: 0;
  }

  .markdown-blockquote:last-child {
    margin-bottom: 0;
  }

  /* Horizontal rule */
  .markdown-hr {
    border: none;
    border-top: 1px solid #e0e0e0;
    margin: 1em 0;
  }

  /* Line breaks */
  .markdown-br {
    display: block;
    content: '';
    margin: 0.25em 0;
  }

  /* Remove extra whitespace between adjacent elements */
  .markdown-p + .markdown-list,
  .markdown-list + .markdown-p,
  .markdown-p + .markdown-blockquote,
  .markdown-blockquote + .markdown-p {
    margin-top: 0.5em;
  }
`;
